import React, { useEffect, useReducer, useState } from 'react';
import {
  DialogContent,
  Button,
  TextField,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  LinearProgress
} from '@mui/material';
import { get } from 'lodash';

import printPrettyPhoneNumber from '../../../../../common/utils/printPrettyPhoneNumber';
import {
  sendEmail,
  sendSMS
} from '../../../../../common/utils/contactCustomer';
import { RetailModal } from '@/RestoreComponent/RetailModal';
import { ModalActions } from '@/RestoreComponent/ModalActions';
import {
  EditClientProfile,
  profileNeedsMissingInfo
} from '@/views/Pages/Customer/CustomerProfile/CustomerTabs/Info/EditClientProfile';
import { formDataInstance } from '@/common/axios';
import CONFIG from '@/config';
import { CustomerDetailsResponse } from '@/common/Selectors/CustomersSearch';
import { normalizeCustomerId } from '@/common/utils/apiConversionUtils';
import { getRestHeaders } from '@/common/utils/api';
import { StaffAppointmentListResponseCustomerProperty } from '@/api/booking/booking-api.schemas';

interface SendWaiverLinkModalProps {
  customer: StaffAppointmentListResponseCustomerProperty | null;
  title: string;
  dataSource: any[];
  onClose?: () => void;
  ErrorMessageComponent?: React.FC<{ errors: string[] }>;
  DescriptionComponent?: React.FC<any>;
  initiallyHideErrorButton?: boolean;
}

type CommunicationType = 'text' | 'email' | 'other';
type State = {
  target: CommunicationType;
  errors: string[];
  otherEmailOrPhone?: string;
  sending?: boolean;
};

const errorMessages = {
  CUSTOMER_MISSING_EMAIL: 'Client missing Email',
  CUSTOMER_MISSING_PHONE: 'Client missing phone',
  CUSTOMER_INVALID_PHONE: 'Client has invalid phone',
  CUSTOMER_TEXTS_UNSUBSCRIBED: 'Client has unsubscribed from texts',
  FAILED_SENDING_TEXT: 'Failed to send text',
  MISSING_OTHER_FIELD: 'Missing Other Method field',
  INVALID_OTHER_FIELD: 'Invalid Other Method field'
};

export const SendWaiverLinkModal: React.FC<SendWaiverLinkModalProps> = ({
  title,
  customer,
  onClose = () => void 0,
  dataSource,
  ErrorMessageComponent,
  DescriptionComponent,
  initiallyHideErrorButton
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [userEditOpen, setUserEditOpen] = useState(false);

  const [state, setState] = useReducer(
    (state: State, action: Partial<State>): State => ({
      ...state,
      ...(action || {})
    }),
    { target: 'email', errors: [], otherEmailOrPhone: '' }
  );
  const { target, errors, otherEmailOrPhone, sending } = state;

  const getCustomerDetails = async () => {
    setLoading(true);
    return formDataInstance
      .get(
        `${CONFIG.USERS_API_URL}/staff/users?customerId=${normalizeCustomerId(
          customer?.id
        )}`
      )
      .then((response: CustomerDetailsResponse) => {
        setCustomerInfo(response.data.data);
        setUserEditOpen(profileNeedsMissingInfo(get(response, 'data.data')));
      })
      .catch(e => {
        console.log(e);
        return null;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCustomerDetails();
  }, []);

  const onElsewhereSent = dataSource => {
    // TODO -- add some validation and an error message
    if (otherEmailOrPhone && otherEmailOrPhone.includes('@')) {
      onEmail(otherEmailOrPhone, dataSource);
    } else {
      if (!otherEmailOrPhone) {
        setState({
          sending: false,
          errors: ['Please provide a valid email or phone number']
        });
      } else {
        onSMS(otherEmailOrPhone, dataSource);
      }
    }
  };

  // Create a data package to send to the backend so a link can be constructed and emailed or texted to the customer
  // By sending both the store and the customer guid, the link can be constructed by the backend without any DB reads
  const createLinkContent = (to: string, what: any) => {
    const content = {
      to: to,
      subject: 'Restore Online Paperwork',
      customer_guid: customerInfo?.guid,
      store_code: JSON.parse(localStorage.getItem('USER'))?.store?.storeCode,
      predefined: JSON.stringify(what)
    };
    return content;
  };

  const onEmail = async (email, dataSource) => {
    const sendToAllPromise = (dataSource || []).map((what, i) => {
      // Make sure whats is an array
      if (errors?.length && !errors[i]) {
        // if email was sent succesfully for this, dont sent it again
        return new Promise(resolve => {
          resolve({});
        });
      }
      return new Promise(resolve =>
        (async () => {
          try {
            const res = await sendEmail(createLinkContent(email, what));
            resolve(res);
          } catch (e) {
            resolve({ error: e });
          }
        })()
      );
    });
    const results = await Promise.all(sendToAllPromise);

    setState({
      sending: false,
      errors: results?.map((r: any) => r?.error || '') || []
    });
  };

  // Callback when send SMS is clicked
  const onSMS = async (phone, dataSource) => {
    const sendToAllPromise = (dataSource || []).map((what, i) => {
      // Make sure whats is an array
      if (errors?.length && !errors[i]) {
        // if SMS was sent succesfully for this, dont sent it again
        return new Promise(resolve => {
          resolve({});
        });
      }
      return new Promise(resolve => {
        sendSMS(createLinkContent(phone, what))
          .then(res => {
            resolve(res);
          })
          .catch(e => ({ error: e?.toString() }));
      });
    });

    const results = await Promise.all(sendToAllPromise);
    setState({
      sending: false,
      errors: results?.map((r: any) => r?.error || '') || []
    });
  };

  const onSubmit = async dataSource => {
    setState({ sending: true, errors: [] });

    const linkType = dataSource[0]?.what;

    if (linkType == 'signup') {
      // signup/waiver link use case
      setLoading(true);
      const customerId = parseInt(`${normalizeCustomerId(customer?.id)}`);

      await formDataInstance
        .post(
          CONFIG.USERS_API_URL + '/staff/send-waiver-link',
          {
            customerId: customerId,
            channel: target,
            other: otherEmailOrPhone
          },
          {
            headers: getRestHeaders()
          }
        )
        .then(res => {
          if (res.data?.data?.success === false) {
            setState({
              sending: false,
              errors: ['There was an error sending the waiver']
            });
          } else {
            setState({
              sending: false,
              errors: ['']
            });
          }
        })
        .catch(e => {
          console.log(e);
          const error_code = e?.response?.data?.error_code;
          setState({
            sending: false,
            errors: [
              errorMessages[error_code] ||
                `There was an error sending the waiver: ${error_code}`
            ]
          });
        });

      setLoading(false);
    } else {
      switch (target) {
        case 'other':
          onElsewhereSent(dataSource);
          break;
        case 'email':
          onEmail(customerInfo?.email, dataSource);
          break;
        case 'text':
          onSMS(customerInfo?.mobilePhone, dataSource);
          break;
        default:
          break;
      }
    }
  };

  const hasError = errors?.some(e => !!e);
  const ErrorComponent = ErrorMessageComponent || DefaultErrorMessageComponent;
  const DescComponent = DescriptionComponent || DefaultDescriptionComponent;

  if (errors?.length && !hasError) {
    // this means all the requests were successfully completed
    onClose?.();
  }

  return (
    <>
      {!loading && !userEditOpen && (
        <RetailModal
          open={true}
          onClose={onClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          title={title}
          loading={sending}
          errorDescription={
            hasError ? <ErrorComponent errors={errors} /> : null
          }
        >
          {loading && <LinearProgress />}
          <DialogContent sx={{ mb: 3, mt: -3 }}>
            <Grid
              display={'flex'}
              alignItems={'flex-start'}
              flexDirection="column"
              gap={1}
              sx={{ mb: 2, mt: 0 }}
            >
              <DescComponent />
            </Grid>
            <Grid
              display={'flex'}
              alignItems={'flex-start'}
              flexDirection="column"
              gap={1}
            >
              <RadioGroup
                aria-label="send"
                name="controlled-radio-buttons-group"
                value={target}
                onChange={e =>
                  setState({
                    errors: [],
                    target: e.target.value as CommunicationType
                  })
                }
              >
                <FormControlLabel
                  value={`email`}
                  control={<Radio sx={{ p: 0.5, m: 0, ml: 1 }} />}
                  label={
                    <Typography variant="body4">{`Email to: ${customerInfo?.email}`}</Typography>
                  }
                />
                <FormControlLabel
                  value={`text`}
                  control={<Radio sx={{ p: 0.5, m: 0, ml: 1 }} />}
                  label={
                    <Typography variant="body4">
                      {`SMS to ${printPrettyPhoneNumber(
                        customerInfo?.mobilePhone
                      )}`}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="other"
                  control={<Radio sx={{ p: 0.5, m: 0, ml: 1 }} />}
                  label={
                    <Typography variant="body4">{`Other Method`}</Typography>
                  }
                />
              </RadioGroup>
              {!customerInfo?.mobilePhone &&
                !customerInfo?.email &&
                'Client has no phone or email on file.'}
              <TextField
                id="elsewhere"
                key="elsewhere"
                label="Enter email or phone"
                size="medium"
                variant="outlined"
                sx={{ ml: 3.5, width: 300 }}
                disabled={target !== 'other'}
                onChange={e => setState({ otherEmailOrPhone: e.target.value })}
              />
            </Grid>
          </DialogContent>
          <ModalActions>
            {!initiallyHideErrorButton || hasError ? (
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
            ) : null}
            <Button
              disabled={sending}
              variant="contained"
              onClick={() => {
                onSubmit(dataSource);
              }}
            >
              Send Agreement
            </Button>
          </ModalActions>
        </RetailModal>
      )}
      {!loading && userEditOpen && (
        <EditClientProfile
          open={userEditOpen}
          handleClose={updated => {
            setUserEditOpen(false);
            if (updated) {
              getCustomerDetails();
            } else {
              onClose();
            }
          }}
          customerInfo={customerInfo}
          errorText="There is missing information on client account for a Membership Agreement."
          titleText="Client Account Update Required"
        />
      )}
    </>
  );
};

const DefaultErrorMessageComponent: React.FC<{ errors: string[] }> = ({
  errors
}) => {
  return <>{errors[0]}</>;
};

const DefaultDescriptionComponent: React.FC = () => {
  return null;
};
